/**
 * HIÁNYOS PROFIL — EGY szabály a Dashboard Profil-fül jelzéséhez és a Felhasználók lista piros soraihoz
 * [Web regisztráció születési dátum, 2026-10-04]. A mobil `mobile/utils/profileCompleteness.ts`
 * (`missingProfileFields`, `ageOn`) mintájára, ugyanazokkal a magyar tétel-nevekkel.
 *
 * MI VOLT A BAJ (olvasva igazolva): a régi `isProfileComplete` a `profile.userRole !== 'Administrator'`-t
 * nézte, de a Dashboard a `userProfileService.getMyProfile()` választ adta neki (`Dashboard.tsx:238-244`),
 * a profil-válaszban pedig NINCS `userRole` — csak `userRoleName` (megjelenített) és `userRoleSystemName`
 * (gépi; `backend/PinyoApi/DTOs/UserProfileDto.cs:30-39`, `types/user.ts:142`). Így a feltétel az ADMINRA is
 * igaz volt → az admin számlázási adat nélkül örökre hiányos-pöttyöt látott. Ráadásul a jelzés nem mondta
 * meg, MI hiányzik.
 *
 * A szerepkör a GÉPI névből dől el (`userRoleSystemName`; tartalék: `userRole` — a felhasználó-LISTA elemén
 * ez a gépi név, `UsersController.cs:90`), mert a megjelenített nevet az admin átnevezheti.
 *
 * Szabály (a mobillal azonos):
 *   - név, e-mail, telefonszám, születési dátum — mindenkinek;
 *   - számlázási név / irányítószám / város / utca — mindenkinek, KIVÉVE az Administrator szerepkört;
 *   - 18 év alatt (a 18. születésnapon már NEM kiskorú) a szülő neve és telefonszáma;
 *   - lakcím NEM számít (klub-döntés, 2026-10-03).
 */

interface ProfileFields {
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  /** A szerepkör gépi neve a felhasználó-lista elemén (`UserListItem.userRole`) — tartalék. */
  userRole?: string | null;
  /** A szerepkör gépi neve a profil-válaszban (`UserProfile.userRoleSystemName`) — az elsődleges. */
  userRoleSystemName?: string | null;
  billingName?: string | null;
  billingZipCode?: string | null;
  billingCity?: string | null;
  billingStreetAddress?: string | null;
  dateOfBirth?: string | null;
  parentName?: string | null;
  parentPhone?: string | null;
}

/**
 * Betöltött kor a megadott napon. A szerver `"2000-05-17T00:00:00"` alakban adja a dátumot: az első 10
 * karakter a NAPTÁRI nap — ezt vesszük (a `new Date(...)` egy „Z" nélküli értéket helyinek, a dátum-only
 * alakot UTC-nek venné: a két alak eltérő napot adhat). Értelmezhetetlen dátumra `null`.
 */
export const ageOn = (dateOfBirth: string, today: Date): number | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateOfBirth);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]) - 1;
  const day = Number(m[3]);
  let age = today.getFullYear() - year;
  const monthDiff = today.getMonth() - month;
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < day)) {
    age--;
  }
  return age;
};

/** A hiányzó KÖTELEZŐ profiladatok magyar neve (üres lista = a profil kész). `today`: injektálható „ma". */
export const missingProfileFields = (profile: ProfileFields, today: Date = new Date()): string[] => {
  const role = profile.userRoleSystemName || profile.userRole || '';
  const needsBilling = role !== 'Administrator';
  const missing: string[] = [];
  if (!profile.name) missing.push('név');
  if (!profile.email) missing.push('e-mail cím');
  if (!profile.phone) missing.push('telefonszám');
  if (!profile.dateOfBirth) missing.push('születési dátum');
  if (needsBilling) {
    if (!profile.billingName) missing.push('számlázási név');
    if (!profile.billingZipCode) missing.push('számlázási irányítószám');
    if (!profile.billingCity) missing.push('számlázási város');
    if (!profile.billingStreetAddress) missing.push('számlázási utca, házszám');
  }
  if (profile.dateOfBirth) {
    const age = ageOn(profile.dateOfBirth, today);
    if (age !== null && age < 18) {
      if (!profile.parentName) missing.push('szülő neve');
      if (!profile.parentPhone) missing.push('szülő telefonszáma');
    }
  }
  return missing;
};

export const isProfileComplete = (profile: ProfileFields, today: Date = new Date()): boolean =>
  missingProfileFields(profile, today).length === 0;

/** „Hiányzó profiladat: születési dátum, számlázási név" (üres listára üres szöveg). */
export const missingProfileText = (missing: string[]): string =>
  missing.length ? `Hiányzó profiladat: ${missing.join(', ')}` : '';
